import { useState, useEffect, useRef } from 'react';
import lcaClient from '../../../../services/lcaClient';

/**
 * Dataset typeahead backed by GET /datasets/search (reference library +
 * legacy system + user datasets, grouped by name with geography variants).
 *
 * The legacy `datasets` prop (54-row catalog already loaded by
 * useInventoryData) renders instantly while the server search is in flight,
 * then server results replace it. Server calls are debounced 300 ms.
 *
 * Option shape handed to onChange (superset of the legacy shape):
 *   { id, name, emission_factor, unit, geography, source_type, library,
 *     variants: [{id, geography, gwp}] }
 */
export function useDatasetSearch(datasets, category, value) {
  const [search, setSearch] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredDatasets, setFilteredDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  // Instant local fallback from the already-loaded legacy catalog.
  const localFilter = (term) => {
    const base = (datasets || []).filter(ds => ds.category === category);
    const matched = term
      ? base.filter(ds => ds.name.toLowerCase().includes(term.toLowerCase()))
      : base;
    return matched.slice(0, 10).map(ds => ({
      id: ds.id,
      name: ds.name,
      emission_factor: ds.emission_factor,
      unit: ds.unit,
      geography: ds.geography,
      source_type: ds.source_type || 'system',
      library: ds.source_type === 'user' ? 'User' : 'Legacy',
      variants: [{ id: ds.id, geography: ds.geography, gwp: ds.emission_factor }],
    }));
  };

  useEffect(() => {
    setFilteredDatasets(localFilter(search));
    const seq = ++requestSeq.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await lcaClient.get('/datasets/search', {
          params: { q: search || undefined, category, limit: 20 },
        });
        if (seq !== requestSeq.current) return; // stale response
        const options = (data || []).map(group => {
          const defaultVariant =
            group.variants.find(v => v.id === group.default_variant_id) ||
            group.variants[0] || {};
          return {
            id: group.default_variant_id || defaultVariant.id,
            name: group.name,
            emission_factor: defaultVariant.gwp,
            unit: group.unit,
            geography: defaultVariant.geography,
            source_type: group.source_type,
            library: group.library,
            variants: group.variants,
          };
        });
        setFilteredDatasets(options);
      } catch (e) {
        // keep the local fallback already rendered
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, datasets, category]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (dataset, onChange) => {
    setSearch(dataset.name);
    onChange(dataset);
    setIsOpen(false);
  };

  const handleInputChange = (e, onChange) => {
    setSearch(e.target.value);
    setIsOpen(true);
    if (e.target.value !== search) {
      onChange({ name: e.target.value, id: null, emission_factor: null });
    }
  };

  return {
    search,
    isOpen, setIsOpen,
    filteredDatasets,
    loading,
    inputRef, dropdownRef,
    handleSelect,
    handleInputChange
  };
}
